import { useMemo, useState, useEffect } from "react";
import Translator from "../../classes/Translator/Translator";
import DemoFormService from "./DemoForm.service";
import validator from "./DemoForm.validator";
import CustomError from "../../classes/CustomError/CustomError";

const DemoFormLogic = (props) => {
  const { translator, currentLang } = Translator();
  const service = DemoFormService();

  const [values, setValues] = useState({
    companyName: "",
    email: "",
    phoneNumber: "",
    ownsAsset: "",
    assetProjectName: "",
    location: "",
    gps: "",
    quantityUnit: "",
    project_type: "",
    project_type_other: "",
    openEnded: "",
  });

  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState(null);
  const [loadingContact, setLoadingContact] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [contactDisabled, setContactDisabled] = useState(false);
  const [contactDone, setContactDone] = useState(false);
  const [submitDone, setSubmitDone] = useState(false);

  const resetForm = () => {
    setValues({
      companyName: "",
      email: "",
      phoneNumber: "",
      ownsAsset: "",
      assetProjectName: "",
      location: "",
      gps: "",
      quantityUnit: "",
      project_type: "",
      project_type_other: "",
      openEnded: "",
    });
    setErrors({});
    setFormSubmitted(false);
    setContactDisabled(false);
    setContactDone(false);
    setSubmitDone(false);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    let updatedValues = { ...values, [name]: value };
    
    if (name === "ownsAsset") {
      if (value === "no") {
        updatedValues = {
          ...updatedValues,
          companyName: "",
          assetProjectName: "",
          location: "",
          gps: "",
          quantityUnit: "",
          project_type: "",
          project_type_other: "",
          openEnded: "",
        };
        setErrors({});
        setContactDisabled(false);
      } else if (value === "yes") {
        const newErrors = { ...errors };
        delete newErrors.error_companyName;
        delete newErrors.error_assetProjectName;
        delete newErrors.error_location;
        delete newErrors.error_gps;
        delete newErrors.error_quantityUnit;
        delete newErrors.error_project_type;
        delete newErrors.error_project_type_other;
        delete newErrors.error_openEnded;
        setErrors(newErrors);
      }
    }

    // If user switches away from "other", clear the custom value.
    if (name === "project_type" && value !== "other") {
      updatedValues = { ...updatedValues, project_type_other: "" };
      const newErrors = { ...errors };
      delete newErrors.error_project_type_other;
      setErrors(newErrors);
    }
    
    setValues(updatedValues);
    const newErrors = validator(
      updatedValues,
      "full",
      translator
    );
    setErrors(newErrors);
  };

  const canContact = useMemo(() => {
    if (values.ownsAsset !== "no" || contactDisabled || contactDone) return false;
    const email = (values.email || "").trim();
    if (email.length === 0) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }, [values.ownsAsset, values.email, contactDisabled, contactDone]);

  const canSubmit = useMemo(() => {
    if (values.ownsAsset !== "yes" || submitDone) return false;
    const requiredKeys = [
      "companyName",
      "email",
      "phoneNumber",
      "assetProjectName",
      "location",
      "gps",
      "quantityUnit",
      "project_type",
      "openEnded",
    ];

    if (values.project_type === "other") {
      requiredKeys.push("project_type_other");
    }

    return requiredKeys.every(
      (key) => (values[key] || "").toString().trim() !== ""
    );
  }, [values, submitDone]);

  useEffect(() => {
    if (values.ownsAsset === "no") {
      const filledFields = Object.entries(values).filter(
        ([key, val]) => 
          key !== "email" && 
          key !== "phoneNumber" && 
          key !== "ownsAsset" &&
          (val || "").toString().trim() !== ""
      );
      if (filledFields.length > 0) {
        setContactDisabled(true);
        if (alert?.type === "success" && alert?.text?.includes("eGreen")) {
          setAlert(null);
        }
      } else {
        setContactDisabled(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  const handleContact = async () => {
    if (values.ownsAsset !== "no" || formSubmitted || contactDisabled || contactDone) return;
    if (!values.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      setAlert({
        type: "error",
        key: "demoForm.alertInvalidEmail",
        text: translator("demoForm.alertInvalidEmail"),
      });
      return;
    }
    setLoadingContact(true);
    try {
      const response = await service.contact(values.email);
      if (!response || response.status >= 400) {
        throw new Error("API Error");
      }

      setAlert({
        type: "success",
        key: "demoForm.alertEmailSuccess",
        text: translator("demoForm.alertEmailSuccess"),
      });
      setContactDone(true);
    } catch (error) {
      console.error("Error:", error);
      if (!navigator.onLine || error.message === "Network Error") {
        setAlert({
          type: "error",
          key: "demoForm.alertNoInternet",
          text:
            translator("demoForm.alertNoInternet") ||
            "No hay conexión a Internet.",
        });
      } else {
        setAlert({
          type: "error",
          key: "demoForm.alertEmailError",
          text: translator("demoForm.alertEmailError"),
        });
      }
    } finally {
      setLoadingContact(false);
    }
  };

  const handleSubmit = async () => {
    if (values.ownsAsset !== "yes" || submitDone) return;
    setLoadingSubmit(true);
    try {
      const newErrors = validator(values, "full", translator);
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        setAlert({
          type: "error",
          key: "demoForm.alertFormError",
          text: translator("demoForm.alertFormError"),
        });
        return;
      }
      const [projectRes, ownerRes] = await Promise.all([
        service.createProject(values),
        service.createOwner(values),
      ]);

      if (
        projectRes instanceof CustomError ||
        ownerRes instanceof CustomError ||
        projectRes?.status >= 400 ||
        ownerRes?.status >= 400
      ) {
        setAlert({
          type: "error",
          key: "demoForm.alertProjectOwnerError",
          text: translator("demoForm.alertProjectOwnerError"),
        });
        return;
      }
      const projectId = projectRes?.response?.data?.id || null;
      const ownerId = ownerRes?.response?.data?.id || null;
      if (!projectId || !ownerId) {
        setAlert({
          type: "error",
          key: "demoForm.alertIdsError",
          text: translator("demoForm.alertIdsError"),
        });
        return;
      }
      const associateRes = await service.associateOwnerProject(
        ownerId,
        projectId
      );
      if (associateRes instanceof CustomError || associateRes?.status >= 400) {
        setAlert({
          type: "error",
          key: "demoForm.alertAssociateError",
          text: translator("demoForm.alertAssociateError"),
        });
        return;
      }
      setAlert({
        type: "success",
        key: "demoForm.alertSubmitSuccess",
        text: (
          <>
            {translator("demoForm.alertSubmitSuccess")}{" "}
            <a
              href="https://dashboard.egreen.com/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1976d2", textDecoration: "underline" }}
            >
              {translator("demoForm.alertAdminPanel")}
            </a>
            .
          </>
        ),
      });

      setFormSubmitted(true);
      setSubmitDone(true);
    } catch (error) {
      console.error("Error:", error);
      if (!navigator.onLine || error.message === "Network Error") {
        setAlert({
          type: "error",
          key: "demoForm.alertNoInternet",
          text:
            translator("demoForm.alertNoInternet") ||
            "No hay conexión a Internet.",
        });
      } else {
        setAlert({
          type: "error",
          key: "demoForm.alertSubmitError",
          text: translator("demoForm.alertSubmitError"),
        });
      }
    } finally {
      setLoadingSubmit(false);
    }
  };

  useEffect(() => {
    if (Object.keys(values).some((k) => values[k])) {
      const newErrors = validator(values, "full", translator);
      setErrors(newErrors);
    }
    if (alert) {
      const translationMap = {
        demoForm: {
          alertInvalidEmail: "demoForm.alertInvalidEmail",
          alertEmailSuccess: "demoForm.alertEmailSuccess",
          alertNoInternet: "demoForm.alertNoInternet",
          alertEmailError: "demoForm.alertEmailError",
          alertFormError: "demoForm.alertFormError",
          alertProjectOwnerError: "demoForm.alertProjectOwnerError",
          alertIdsError: "demoForm.alertIdsError",
          alertAssociateError: "demoForm.alertAssociateError",
          alertSubmitError: "demoForm.alertSubmitError",
          alertGenericError: "demoForm.alertGenericError",
          alertSubmitSuccess: "demoForm.alertSubmitSuccess",
          alertAdminPanel: "demoForm.alertAdminPanel",
        },
      };
      if (alert.key === "demoForm.alertSubmitSuccess") {
        setAlert({
          ...alert,
          text: (
            <>
              {translator("demoForm.alertSubmitSuccess")}{" "}
              <a
                href="https://dashboard.egreen.com/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#1976d2", textDecoration: "underline" }}
              >
                {translator("demoForm.alertAdminPanel")}
              </a>
              .
            </>
          ),
        });
        return;
      }

      if (!alert.text?.props) {
        const keyToTranslate = alert.key?.split(".").pop();
        const translatedText =
          translator(translationMap.demoForm[keyToTranslate]) ||
          translator("demoForm.alertGenericError");

        setAlert({
          ...alert,
          text: translatedText,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translator, currentLang, values]);

  useEffect(() => {
    if (alert) {
      const timer = setTimeout(() => {
        if (alert.type === "success") {
          resetForm();
        }
        setAlert(null);
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [alert]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const initializeSession = async () => {
      try {
        const response = await service.createSession();
        console.log("Sesión creada exitosamente:", response);
      } catch (error) {
        console.error("Error creating session en useEffect:", error);
      }
    };

    if (service && service.createSession) {
      initializeSession();
    } else {
      console.error("Service o createSession no disponible");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    translator,
    values,
    errors,
    alert,
    handleChange,
    canContact,
    canSubmit,
    handleContact,
    handleSubmit,
    loadingContact,
    loadingSubmit,
    formSubmitted,
  };
};

export default DemoFormLogic;
